import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, ASSET_TYPE_LABELS, LIABILITY_TYPE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, PencilSimple } from "@phosphor-icons/react";

/**
 * Universal creation dialog used by every "File to" dropdown in the ingest flow.
 * Opens pre-filled with parsed document data; user can review / edit, then accept.
 *
 * Props:
 *   open           – controlled open state
 *   onClose        – called on cancel / backdrop click
 *   onCreate       – called with the new record's `id` after successful creation
 *   kind           – "liability" | "real_estate" | "mineral_royalty" | "unit"
 *   parsed         – the AI-parsed data bag from the ingest item
 *   hid            – active household ID
 *   assets         – asset list (for linking a mortgage to a property)
 *   parentAssetId  – when kind="unit", the owning asset ID
 */
export default function IngestCreateDialog({ open, onClose, onCreate, kind, parsed = {}, hid, assets = [], parentAssetId }) {
  const p = parsed || {};
  const [saving, setSaving] = useState(false);

  // ── Form fields — initialised from parsed data ──
  const [f, setF] = useState({});

  // Re-initialise whenever the dialog opens with new parsed data
  const parsedSig = JSON.stringify(parsed || {});
  useEffect(() => {
    if (!open) return;
    if (kind === "liability") {
      setF({
        name: p.lender ? `${p.lender} Mortgage` : (p.property_address ? `Mortgage – ${p.property_address}` : "New Mortgage"),
        liability_type: "mortgage",
        lender: p.lender || "",
        loan_number: p.loan_number || "",
        original_balance: "",
        current_balance: p.current_balance ?? "",
        interest_rate: "",
        monthly_payment: p.monthly_payment ?? "",
        due_date: p.loan_due_date || "",
        asset_id: "",
        owner_party: "joint",
      });
    } else if (kind === "real_estate") {
      setF({
        name: p.property_address || "New Property",
        address: p.property_address || "",
        current_value: "",
        owner_party: "joint",
        entity_name: p.entity_name || "",
      });
    } else if (kind === "mineral_royalty") {
      const msum = p._mineral_summary || {};
      const hdr = msum.header || {};
      setF({
        name: hdr.operator || p.payor || "New Mineral Package",
        owner_code: hdr.owner_code || "",
        operator: hdr.operator || "",
        owner_party: "joint",
      });
    } else if (kind === "unit") {
      setF({
        label: p.tenant_name ? `Unit – ${p.tenant_name}` : "New Unit",
        monthly_rent: p.monthly_rent ?? "",
      });
    }
  }, [open, kind, parsedSig]); // re-init when dialog opens or parsed data changes

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (kind === "liability") {
        const r = await api.post(`/liabilities?household_id=${hid}`, {
          name: f.name?.trim() || "New Mortgage",
          liability_type: f.liability_type || "mortgage",
          lender: f.lender || "",
          loan_number: f.loan_number || "",
          original_balance: f.original_balance === "" ? 0 : Number(f.original_balance),
          current_balance: f.current_balance === "" ? 0 : Number(f.current_balance),
          interest_rate: f.interest_rate === "" ? null : Number(f.interest_rate),
          monthly_payment: f.monthly_payment === "" ? null : Number(f.monthly_payment),
          due_date: f.due_date || null,
          asset_id: f.asset_id || null,
          owner_party: f.owner_party || "joint",
        });
        toast.success(`Created "${r.data.name}"`);
        onCreate?.(r.data.id);
      } else if (kind === "real_estate") {
        const r = await api.post(`/assets?household_id=${hid}`, {
          name: f.name?.trim() || "New Property",
          asset_type: "real_estate",
          address: f.address || "",
          current_value: f.current_value === "" ? 0 : Number(f.current_value),
          owner_party: f.owner_party || "joint",
          confidence: "low",
          review_status: "needs_review",
        });
        toast.success(`Created "${r.data.name}"`);
        onCreate?.(r.data.id);
      } else if (kind === "mineral_royalty") {
        const r = await api.post(`/assets?household_id=${hid}`, {
          name: f.name?.trim() || "New Mineral Package",
          asset_type: "mineral_royalty",
          owner_party: f.owner_party || "joint",
          current_value: 0,
          confidence: "low",
          review_status: "needs_review",
          meta: { owner_code: f.owner_code || "", operator: f.operator || "" },
        });
        toast.success(`Created "${r.data.name}"`);
        onCreate?.(r.data.id);
      } else if (kind === "unit") {
        const r = await api.post(`/assets/${parentAssetId}/units?household_id=${hid}`, {
          label: f.label?.trim() || "New Unit",
          monthly_rent: f.monthly_rent === "" ? 0 : Number(f.monthly_rent),
        });
        toast.success(`Created unit "${r.data.label || f.label}"`);
        onCreate?.(r.data.id);
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create");
    } finally {
      setSaving(false);
    }
  };

  const titles = {
    liability: "New Mortgage / Loan",
    real_estate: "New Property",
    mineral_royalty: "New Mineral Package",
    unit: "New Unit",
  };
  const descriptions = {
    liability: "Pre-filled from the uploaded statement. Review, edit as needed, then accept.",
    real_estate: "Pre-filled from the uploaded document. Review, edit as needed, then accept.",
    mineral_royalty: "Pre-filled from the uploaded statement. Review, edit as needed, then accept.",
    unit: "Pre-filled from the uploaded lease. Review, edit as needed, then accept.",
  };

  const reAssets = (assets || []).filter((a) => a.asset_type === "real_estate");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="ingest-create-dialog"
        className="sm:max-w-lg bg-[hsl(var(--ploy-card))] border-[hsl(var(--ploy-border))]"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl tracking-tight text-[hsl(var(--ploy-ink))]">
            {titles[kind] || "New Record"}
          </DialogTitle>
          <DialogDescription className="text-[hsl(var(--ploy-ink-soft))] text-sm">
            {descriptions[kind] || "Review the pre-filled data and accept."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
          {kind === "liability" && <LiabilityForm f={f} set={set} reAssets={reAssets} />}
          {kind === "real_estate" && <RealEstateForm f={f} set={set} />}
          {kind === "mineral_royalty" && <MineralForm f={f} set={set} />}
          {kind === "unit" && <UnitForm f={f} set={set} />}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={onClose} data-testid="ingest-create-cancel" className="text-[hsl(var(--ploy-ink-soft))]">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="ingest-create-accept"
            className="bg-[hsl(var(--ploy-ink))] text-[hsl(var(--ploy-ink-inv))] hover:bg-[hsl(var(--ploy-ink))]/90 rounded-[10px] font-semibold gap-1.5"
          >
            <CheckCircle size={15} weight="fill" />
            {saving ? "Creating…" : "Accept & Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Field helper ──
const Field = ({ label, testid, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wider font-bold text-[hsl(var(--ploy-brass-deep))]">{label}</Label>
    <div className="mt-1" data-testid={testid}>{children}</div>
  </div>
);

// ── Liability / mortgage form ──
const LiabilityForm = ({ f, set, reAssets }) => (
  <div className="space-y-3">
    <Field label="Name" testid="create-name">
      <Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-sm h-9" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Lender / Servicer" testid="create-lender">
        <Input value={f.lender || ""} onChange={(e) => set("lender", e.target.value)} className="rounded-sm h-9" />
      </Field>
      <Field label="Loan / Account #" testid="create-loan-number">
        <Input value={f.loan_number || ""} onChange={(e) => set("loan_number", e.target.value)} className="rounded-sm h-9 font-mono" />
      </Field>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <Field label="Current Balance" testid="create-balance">
        <Input type="number" value={f.current_balance ?? ""} onChange={(e) => set("current_balance", e.target.value)} className="rounded-sm h-9 font-mono" />
      </Field>
      <Field label="Monthly Payment" testid="create-payment">
        <Input type="number" value={f.monthly_payment ?? ""} onChange={(e) => set("monthly_payment", e.target.value)} className="rounded-sm h-9 font-mono" />
      </Field>
      <Field label="Interest Rate %" testid="create-rate">
        <Input type="number" step="0.01" value={f.interest_rate ?? ""} onChange={(e) => set("interest_rate", e.target.value)} className="rounded-sm h-9 font-mono" />
      </Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Next Due Date" testid="create-due">
        <Input type="date" value={f.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="rounded-sm h-9" />
      </Field>
      <Field label="Owner" testid="create-owner">
        <Select value={f.owner_party || "joint"} onValueChange={(v) => set("owner_party", v)}>
          <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="joint">Joint</SelectItem>
            <SelectItem value="A">A</SelectItem>
            <SelectItem value="B">B</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
    {reAssets.length > 0 && (
      <Field label="Secured by property (optional)" testid="create-linked-asset">
        <Select value={f.asset_id || "__none__"} onValueChange={(v) => set("asset_id", v === "__none__" ? "" : v)}>
          <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Link to property" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {reAssets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
    )}
  </div>
);

// ── Real estate form ──
const RealEstateForm = ({ f, set }) => (
  <div className="space-y-3">
    <Field label="Property Name" testid="create-name">
      <Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-sm h-9" />
    </Field>
    <Field label="Address" testid="create-address">
      <Input value={f.address || ""} onChange={(e) => set("address", e.target.value)} className="rounded-sm h-9" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Estimated Value" testid="create-value">
        <Input type="number" value={f.current_value ?? ""} onChange={(e) => set("current_value", e.target.value)} className="rounded-sm h-9 font-mono" />
      </Field>
      <Field label="Owner" testid="create-owner">
        <Select value={f.owner_party || "joint"} onValueChange={(v) => set("owner_party", v)}>
          <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="joint">Joint</SelectItem>
            <SelectItem value="A">A</SelectItem>
            <SelectItem value="B">B</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  </div>
);

// ── Mineral royalty form ──
const MineralForm = ({ f, set }) => (
  <div className="space-y-3">
    <Field label="Package Name" testid="create-name">
      <Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="rounded-sm h-9" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Owner Code" testid="create-owner-code">
        <Input value={f.owner_code || ""} onChange={(e) => set("owner_code", e.target.value)} className="rounded-sm h-9 font-mono" />
      </Field>
      <Field label="Operator" testid="create-operator">
        <Input value={f.operator || ""} onChange={(e) => set("operator", e.target.value)} className="rounded-sm h-9" />
      </Field>
    </div>
    <Field label="Owner" testid="create-owner">
      <Select value={f.owner_party || "joint"} onValueChange={(v) => set("owner_party", v)}>
        <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="joint">Joint</SelectItem>
          <SelectItem value="A">A</SelectItem>
          <SelectItem value="B">B</SelectItem>
        </SelectContent>
      </Select>
    </Field>
  </div>
);

// ── Unit form (for lease filing) ──
const UnitForm = ({ f, set }) => (
  <div className="space-y-3">
    <Field label="Unit Label" testid="create-label">
      <Input value={f.label || ""} onChange={(e) => set("label", e.target.value)} className="rounded-sm h-9" />
    </Field>
    <Field label="Monthly Rent" testid="create-rent">
      <Input type="number" value={f.monthly_rent ?? ""} onChange={(e) => set("monthly_rent", e.target.value)} className="rounded-sm h-9 font-mono" />
    </Field>
  </div>
);
